import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { createOperationStudent } from '../../services/operationStudentService';
import { FileSpreadsheet, Download, Search, Plus, Trash2, Save, X, Edit2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useTablePreferences } from '../../hooks/useTablePreferences';
import * as XLSX from 'xlsx';

interface MedicalStudentListProps {
    programType: string;
}

interface MedicalApplication {
    id: string; // unique id for the application
    university: string;
    exam_username?: string;
    exam_password?: string;
    status?: string;
    payment?: string;
    exam_date?: string;
    result?: string;
    portal_username?: string;
    portal_password?: string;
    registration_status?: string;
}

interface Student {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    medical_applications: MedicalApplication[];
    created_at: string;
    notes?: string;
}

const MEDICAL_UNIVERSITIES = [
    'Humanitas',
    'Humanitas Medtech',
    'San Raffaele',
    'Cattolica',
    'Biomedico',
    'Biomedico Medtech',
    'UniCamillus',
    'Saint Camillus'
];

const STATUS_OPTIONS = [
    'Sınav kaydı alındı',
    'Ödeme Bekliyor',
    'Evrak Bekliyor',
    'Tamamlandı',
    'İptal'
];

const MedicalStudentList: React.FC<MedicalStudentListProps> = ({ programType }) => {
    const [loading, setLoading] = useState(true);
    const [students, setStudents] = useState<Student[]>([]);
    const [searchTerm, setSearchTerm] = useState('');

    // Add Student State
    const { user } = useAuth();
    const [newStudent, setNewStudent] = useState({
        first_name: '',
        last_name: '',
        university: '',
        exam_username: '',
        exam_password: '',
        status: 'Sınav kaydı alındı',
        payment: '',
        exam_date: '',
        result: '',
        portal_username: '',
        portal_password: '',
        registration_status: ''
    });
    const [isAdding, setIsAdding] = useState(false);
    const [availableUniversities, setAvailableUniversities] = useState<{ id: string; name: string }[]>([]);

    // Column Resizing Logic
    const { columnWidths, setColumnWidths } = useTablePreferences('medical-student-list', {
        first_name: 150,
        last_name: 150,
        university: 200,
        exam_username: 150,
        exam_password: 150,
        status: 150,
        payment: 150,
        exam_date: 150,
        result: 150,
        portal_username: 150,
        portal_password: 150,
        registration_status: 150,
        actions: 100
    });

    const handleMouseDown = (e: React.MouseEvent, key: string) => {
        const startX = e.clientX;
        const startWidth = columnWidths[key];

        const handleMouseMove = (moveEvent: MouseEvent) => {
            const newWidth = Math.max(50, startWidth + (moveEvent.clientX - startX));
            setColumnWidths(prev => ({ ...prev, [key]: newWidth }));
        };

        const handleMouseUp = () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    };

    useEffect(() => {
        const fetchUnis = async () => {
            const { data } = await supabase.from('universities').select('id, name').order('name');
            if (data) setAvailableUniversities(data);
        };
        fetchUnis();
    }, []);

    useEffect(() => {
        fetchStudents();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [programType]);

    const getPageTitle = () => {
        // Map programType to title
        const titles: Record<string, string> = {
            'all-medical': 'Tüm Tıp Öğrencileri',
            'san-raffaele': 'San Raffaele Başvuruları',
            'humanitas': 'Humanitas Başvuruları',
            'cattolica': 'Cattolica Başvuruları',
            'biomedico': 'Biomedico Başvuruları',
            'unicamillus': 'UniCamillus Başvuruları'
        };
        return titles[programType] || 'Tıp Öğrenci Listesi';
    };

    const fetchStudents = async () => {
        setLoading(true);
        try {
            let query = supabase
                .from('operation_students')
                .select('*')
                .order('created_at', { ascending: false });

            if (user?.branchId) query = query.eq('branch_id', user.branchId);
            const { data, error } = await query;

            if (error) throw error;

            // Filter logic: Only include Medical Students
            const medicalKeywords = ['medicine', 'tıp', 'med', 'humanitas', 'san raffaele', 'cattolica', 'biomedico', 'unicamillus', 'saint camillus'];

            const filteredAndMapped = (data || [])
                .filter(s => {
                    const notes = (s.notes || '').toLowerCase();
                    const hasMedicalNote = medicalKeywords.some(k => notes.includes(k));
                    const hasMedicalApps = Array.isArray(s.medical_applications) && s.medical_applications.length > 0;
                    return hasMedicalNote || hasMedicalApps;
                })
                .map(s => ({
                    ...s,
                    medical_applications: Array.isArray(s.medical_applications) ? s.medical_applications : []
                }));

            setStudents(filteredAndMapped);
        } catch (error) {
            console.error('Error fetching students:', error);
        } finally {
            setLoading(false);
        }
    };

    const updateStudentState = (studentId: string, field: string, value: any) => {
        setStudents(prev => prev.map(s => {
            if (s.id !== studentId) return s;
            // @ts-ignore
            return { ...s, [field]: value };
        }));
    };

    const updateAppState = (studentId: string, appId: string, field: string, value: any) => {
        setStudents(prev => prev.map(s => {
            if (s.id !== studentId) return s;
            const updatedApps = s.medical_applications.map(app => {
                if (app.id !== appId) return app;
                // @ts-ignore
                return { ...app, [field]: value };
            });
            return { ...s, medical_applications: updatedApps };
        }));
    };

    const handleSave = async (student: Student) => {
        try {
            const { error } = await supabase
                .from('operation_students')
                .update({
                    first_name: student.first_name,
                    last_name: student.last_name,
                    medical_applications: student.medical_applications
                })
                .eq('id', student.id);

            if (error) throw error;
            alert('Kayıt güncellendi.');
        } catch (error) {
            console.error('Error saving:', error);
            alert('Kaydetme hatası.');
        }
    };

    const handleDelete = async (studentId: string, appId: string) => {
        if (!await window.appConfirm('Bu başvuruyu silmek istediğinize emin misiniz?')) return;

        const student = students.find(s => s.id === studentId);
        if (!student) return;

        const updatedApps = student.medical_applications.filter(app => app.id !== appId);

        try {
            const { error } = await supabase
                .from('operation_students')
                .update({ medical_applications: updatedApps })
                .eq('id', studentId);

            if (error) throw error;

            setStudents(prev => prev.map(s => s.id === studentId ? { ...s, medical_applications: updatedApps } : s));
        } catch (error) {
            console.error('Error deleting:', error);
        }
    };

    const handleAddApplication = async (studentId: string) => {
        const student = students.find(s => s.id === studentId);
        if (!student) return;

        const newApp: MedicalApplication = {
            id: crypto.randomUUID(),
            university: MEDICAL_UNIVERSITIES[0],
            status: 'Sınav kaydı alındı'
        };

        const updatedApps = [newApp, ...student.medical_applications];

        try {
            const { error } = await supabase
                .from('operation_students')
                .update({ medical_applications: updatedApps })
                .eq('id', studentId);

            if (error) throw error;

            setStudents(prev => prev.map(s => s.id === studentId ? { ...s, medical_applications: updatedApps } : s));
        } catch (error) {
            console.error('Error adding application:', error);
            alert('Ekleme başarısız oldu.');
        }
    };

    const handleAddStudent = async () => {
        if (!newStudent.first_name || !newStudent.last_name) {
            alert('Lütfen ad ve soyad giriniz.');
            return;
        }

        try {
            setIsAdding(true);

            // Determine university/program based on current view info if possible, or filtered one
            let uniName = 'General Medical';
            let progName = 'Medicine';
            let pType = 'medicine';

            if (programType !== 'all-medical') {
                uniName = programType.charAt(0).toUpperCase() + programType.slice(1).replace('-', ' ');
                const matched = MEDICAL_UNIVERSITIES.find(u => u.toLowerCase().includes(uniName.toLowerCase()));
                if (matched) uniName = matched;
            } else {
                uniName = 'Humanitas';
            }

            const { data, error } = await createOperationStudent({
                p_first_name: newStudent.first_name,
                p_last_name: newStudent.last_name,
                p_branch_id: user?.branchId,
                p_program_type: pType,
                p_university_name: newStudent.university || uniName, // Use selected university if available
                p_program_name: progName,
                p_created_by: user?.id
            });

            if (error) throw error;
            if (data && !data.success) throw new Error(data.error);

            const operationStudentId = data?.operation_id || data?.id;

            // If extra fields are filled, update the newly created student's application
            if (operationStudentId) {
                // Create the application object with filled details
                const newApp: MedicalApplication = {
                    id: crypto.randomUUID(),
                    university: newStudent.university || uniName,
                    exam_username: newStudent.exam_username,
                    exam_password: newStudent.exam_password,
                    status: newStudent.status,
                    payment: newStudent.payment,
                    exam_date: newStudent.exam_date,
                    result: newStudent.result,
                    portal_username: newStudent.portal_username,
                    portal_password: newStudent.portal_password,
                    registration_status: newStudent.registration_status
                };

                // Update the student with this application
                // Note: The RPC might have already created a default application. 
                // We should overwrite or update it. Since we don't know exact RPC behavior,
                // let's fetch the student first to see what's there, or just overwrite if empty.
                // Safest bet is to just update the medical_applications array with our new detailed app.

                const { error: updateError } = await supabase
                    .from('operation_students')
                    .update({
                        medical_applications: [newApp]
                    })
                    .eq('id', operationStudentId);

                if (updateError) console.error('Error updating new student details:', updateError);
            }

            // Refetch to refresh list
            await fetchStudents();
            setNewStudent({
                first_name: '',
                last_name: '',
                university: '',
                exam_username: '',
                exam_password: '',
                status: 'Sınav kaydı alındı',
                payment: '',
                exam_date: '',
                result: '',
                portal_username: '',
                portal_password: '',
                registration_status: ''
            });
            alert('Öğrenci başarıyla eklendi.');

        } catch (error: any) {
            console.error('Error adding student:', error);
            alert('Öğrenci eklenirken hata: ' + error.message);
        } finally {
            setIsAdding(false);
        }
    };

    const handleExportExcel = () => {
        const flattenData = students.flatMap(s => {
            const apps = getFilteredApplications(s);
            if (apps.length === 0) return [];
            return apps.map(app => ({
                'AD': s.first_name,
                'SOYAD': s.last_name,
                'Üniversite': app.university,
                'SINAV KULLANICI ADI': app.exam_username,
                'SINAV ŞİFRE': app.exam_password,
                'DURUMU': app.status,
                'Ödeme': app.payment,
                'Sınav Tarihi': app.exam_date,
                'SONUÇ': app.result,
                'PORTAL KULLANICI': app.portal_username,
                'PORTAL ŞİFRE': app.portal_password,
                'KAYIT DURUMU': app.registration_status
            }));
        });

        const ws = XLSX.utils.json_to_sheet(flattenData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Medical Applications");
        XLSX.writeFile(wb, `${programType}_medical_students.xlsx`);
    };

    // Filter applications based on programType
    const getFilteredApplications = (student: Student) => {
        if (programType === 'all-medical') return student.medical_applications;

        // Normalize checking
        const search = programType.replace('-', ' ').toLowerCase();
        return student.medical_applications.filter(app =>
            app.university.toLowerCase().includes(search) ||
            (search === 'biomedico' && app.university.toLowerCase().includes('biomedico')) // handle matches
        );
    };

    const filteredStudents = students.filter(s => {
        const matchesSearch = `${s.first_name} ${s.last_name}`.toLowerCase().includes(searchTerm.toLowerCase());
        const hasApps = getFilteredApplications(s).length > 0;

        if (programType === 'all-medical') {
            return matchesSearch;
        }
        return matchesSearch && hasApps;
    });

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="p-6 bg-gray-50 dark:bg-gray-900 min-h-screen">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
                        <FileSpreadsheet className="w-6 h-6 text-blue-600" />
                        {getPageTitle()}
                    </h1>
                </div>
                <div className="flex items-center gap-3">
                    <div className="relative">
                        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Öğrenci Ara..."
                            className="pl-9 pr-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <button
                        onClick={handleExportExcel}
                        className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium transition-colors"
                    >
                        <Download className="w-4 h-4" />
                        Excel İndir
                    </button>
                </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left border-collapse" style={{ tableLayout: 'fixed' }}>
                        <thead className="text-xs text-center text-gray-700 uppercase bg-green-100 dark:bg-green-900/30 dark:text-gray-400">
                            <tr>
                                {[
                                    { key: 'first_name', label: 'AD' },
                                    { key: 'last_name', label: 'SOYADI' },
                                    { key: 'university', label: 'Üniversite' },
                                    { key: 'exam_username', label: 'SINAV KULLANICI ADI' },
                                    { key: 'exam_password', label: 'SINAV ŞİFRE' },
                                    { key: 'status', label: 'DURUMU' },
                                    { key: 'payment', label: 'Ödeme' },
                                    { key: 'exam_date', label: 'Sınav Tarihi' },
                                    { key: 'result', label: 'SONUÇ' },
                                    { key: 'portal_username', label: 'PORTAL KULLANICI' },
                                    { key: 'portal_password', label: 'PORTAL ŞİFRE' },
                                    { key: 'registration_status', label: 'KAYIT DURUMU' },
                                    { key: 'actions', label: '#' }
                                ].map((col) => (
                                    <th
                                        key={col.key}
                                        className="relative px-4 py-3 border border-gray-200 dark:border-gray-700 overflow-hidden text-ellipsis whitespace-nowrap group"
                                        style={{ width: columnWidths[col.key] }}
                                    >
                                        {col.label}
                                        <div
                                            className="absolute right-0 top-0 h-full w-1 cursor-col-resize hover:bg-blue-500 opacity-0 group-hover:opacity-100 transition-opacity"
                                            onMouseDown={(e) => handleMouseDown(e, col.key)}
                                        />
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                            {/* Add New Row */}
                            <tr className="bg-blue-50/50 dark:bg-blue-900/10 border-b border-blue-100 dark:border-blue-900/30">
                                <td className="p-2 border-r dark:border-gray-700">
                                    <input
                                        placeholder="Ad"
                                        className="w-full text-sm p-1 border rounded"
                                        value={newStudent.first_name}
                                        onChange={e => setNewStudent({ ...newStudent, first_name: e.target.value })}
                                    />
                                </td>
                                <td className="p-2 border-r dark:border-gray-700">
                                    <input
                                        placeholder="Soyad"
                                        className="w-full text-sm p-1 border rounded"
                                        value={newStudent.last_name}
                                        onChange={e => setNewStudent({ ...newStudent, last_name: e.target.value })}
                                    />
                                </td>
                                <td className="px-2 py-2 border border-gray-200 dark:border-gray-700 bg-red-50/50 dark:bg-red-900/5 p-1">
                                    <select
                                        className="w-full bg-white dark:bg-gray-800 border rounded py-1 px-1 text-xs focus:ring-1 focus:ring-blue-500"
                                        value={newStudent.university}
                                        onChange={e => setNewStudent({ ...newStudent, university: e.target.value })}
                                    >
                                        <option value="">Seçiniz</option>
                                        {availableUniversities.length > 0 ? (
                                            availableUniversities.map(u => <option key={u.id} value={u.name}>{u.name}</option>)
                                        ) : (
                                            MEDICAL_UNIVERSITIES.map(u => <option key={u} value={u}>{u}</option>)
                                        )}
                                    </select>
                                </td>
                                <td className="px-2 py-2 border border-gray-200 dark:border-gray-700 p-1">
                                    <input
                                        placeholder="Kullanıcı Adı"
                                        className="w-full text-sm p-1 border rounded"
                                        value={newStudent.exam_username}
                                        onChange={e => setNewStudent({ ...newStudent, exam_username: e.target.value })}
                                    />
                                </td>
                                <td className="px-2 py-2 border border-gray-200 dark:border-gray-700 bg-red-50/50 dark:bg-red-900/5 p-1">
                                    <input
                                        placeholder="Şifre"
                                        className="w-full text-sm p-1 border rounded"
                                        value={newStudent.exam_password}
                                        onChange={e => setNewStudent({ ...newStudent, exam_password: e.target.value })}
                                    />
                                </td>
                                <td className="px-2 py-2 border border-gray-200 dark:border-gray-700 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 font-medium p-1">
                                    <select
                                        className="w-full bg-white dark:bg-gray-800 border rounded py-1 px-1 text-xs focus:ring-1 focus:ring-blue-500"
                                        value={newStudent.status}
                                        onChange={e => setNewStudent({ ...newStudent, status: e.target.value })}
                                    >
                                        {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                                        <option value="Diğer">Diğer</option>
                                    </select>
                                </td>
                                <td className="px-2 py-2 border border-gray-200 dark:border-gray-700 p-1">
                                    <input
                                        placeholder="Ödeme"
                                        className="w-full text-sm p-1 border rounded"
                                        value={newStudent.payment}
                                        onChange={e => setNewStudent({ ...newStudent, payment: e.target.value })}
                                    />
                                </td>
                                <td className="px-2 py-2 border border-gray-200 dark:border-gray-700 bg-red-50/50 dark:bg-red-900/5 p-1">
                                    <input
                                        placeholder="Tarih"
                                        className="w-full text-sm p-1 border rounded"
                                        value={newStudent.exam_date}
                                        onChange={e => setNewStudent({ ...newStudent, exam_date: e.target.value })}
                                    />
                                </td>
                                <td className="px-2 py-2 border border-gray-200 dark:border-gray-700 p-1">
                                    <input
                                        placeholder="Sonuç"
                                        className="w-full text-sm p-1 border rounded"
                                        value={newStudent.result}
                                        onChange={e => setNewStudent({ ...newStudent, result: e.target.value })}
                                    />
                                </td>
                                <td className="px-2 py-2 border border-gray-200 dark:border-gray-700 bg-red-50/50 dark:bg-red-900/5 p-1">
                                    <input
                                        placeholder="P. Kul. Adı"
                                        className="w-full text-sm p-1 border rounded"
                                        value={newStudent.portal_username}
                                        onChange={e => setNewStudent({ ...newStudent, portal_username: e.target.value })}
                                    />
                                </td>
                                <td className="px-2 py-2 border border-gray-200 dark:border-gray-700 p-1">
                                    <input
                                        placeholder="P. Şifre"
                                        className="w-full text-sm p-1 border rounded"
                                        value={newStudent.portal_password}
                                        onChange={e => setNewStudent({ ...newStudent, portal_password: e.target.value })}
                                    />
                                </td>
                                <td className="px-2 py-2 border border-gray-200 dark:border-gray-700 bg-red-50/50 dark:bg-red-900/5 p-1">
                                    <input
                                        placeholder="Kayıt Durumu"
                                        className="w-full text-sm p-1 border rounded"
                                        value={newStudent.registration_status}
                                        onChange={e => setNewStudent({ ...newStudent, registration_status: e.target.value })}
                                    />
                                </td>
                                <td className="p-2 text-center">
                                    <button
                                        onClick={handleAddStudent}
                                        disabled={isAdding}
                                        className="bg-blue-600 text-white p-1.5 rounded hover:bg-blue-700 disabled:opacity-50"
                                        title="Ekle"
                                    >
                                        <Plus size={16} />
                                    </button>
                                </td>
                            </tr>
                            {filteredStudents.map(student => {
                                const apps = getFilteredApplications(student);

                                return (
                                    <React.Fragment key={student.id}>
                                        {apps.length === 0 ? (
                                            <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                                                <td className="px-4 py-3 border border-gray-200 dark:border-gray-700 font-medium bg-red-50 dark:bg-red-900/10">
                                                    {student.first_name}
                                                </td>
                                                <td className="px-4 py-3 border border-gray-200 dark:border-gray-700 font-medium bg-red-50 dark:bg-red-900/10">
                                                    {student.last_name}
                                                </td>
                                                <td colSpan={10} className="px-4 py-3 border border-gray-200 dark:border-gray-700 text-center text-gray-400">
                                                    Başvuru bulunmuyor
                                                </td>
                                                <td className="px-4 py-3 border border-gray-200 dark:border-gray-700 text-center">
                                                    <button
                                                        onClick={() => handleAddApplication(student.id)}
                                                        className="p-1 text-green-600 hover:bg-green-100 rounded"
                                                        title="Başvuru Ekle"
                                                    >
                                                        <Plus size={18} />
                                                    </button>
                                                </td>
                                            </tr>
                                        ) : (
                                            apps.map((app, index) => {
                                                return (
                                                    <tr key={app.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 group">
                                                        {index === 0 && (
                                                            <>
                                                                <td rowSpan={apps.length} className="px-4 py-3 border border-gray-200 dark:border-gray-700 font-medium align-middle bg-red-50 dark:bg-red-900/10 p-0">
                                                                    <input
                                                                        type="text"
                                                                        className="w-full h-full px-2 py-3 bg-transparent border-transparent hover:border-gray-300 focus:border-blue-500 rounded outline-none font-medium uppercase"
                                                                        value={student.first_name}
                                                                        onChange={e => updateStudentState(student.id, 'first_name', e.target.value)}
                                                                    />
                                                                </td>
                                                                <td rowSpan={apps.length} className="px-4 py-3 border border-gray-200 dark:border-gray-700 font-medium align-middle bg-red-50 dark:bg-red-900/10 p-0">
                                                                    <input
                                                                        type="text"
                                                                        className="w-full h-full px-2 py-3 bg-transparent border-transparent hover:border-gray-300 focus:border-blue-500 rounded outline-none font-medium uppercase"
                                                                        value={student.last_name}
                                                                        onChange={e => updateStudentState(student.id, 'last_name', e.target.value)}
                                                                    />
                                                                </td>
                                                            </>
                                                        )}

                                                        <td className="px-2 py-2 border border-gray-200 dark:border-gray-700 bg-red-50/50 dark:bg-red-900/5 p-1">
                                                            <select
                                                                className="w-full bg-white dark:bg-gray-800 border rounded py-1 px-1 text-xs focus:ring-1 focus:ring-blue-500"
                                                                value={app.university}
                                                                onChange={e => updateAppState(student.id, app.id, 'university', e.target.value)}
                                                            >
                                                                {availableUniversities.length > 0 ? (
                                                                    availableUniversities.map(u => <option key={u.id} value={u.name}>{u.name}</option>)
                                                                ) : (
                                                                    MEDICAL_UNIVERSITIES.map(u => <option key={u} value={u}>{u}</option>)
                                                                )}
                                                            </select>
                                                        </td>

                                                        <td className="px-2 py-2 border border-gray-200 dark:border-gray-700 p-1">
                                                            <input type="text" className="w-full h-7 bg-white border rounded px-1 text-xs focus:ring-1 focus:ring-blue-500"
                                                                value={app.exam_username || ''} onChange={e => updateAppState(student.id, app.id, 'exam_username', e.target.value)} />
                                                        </td>

                                                        <td className="px-2 py-2 border border-gray-200 dark:border-gray-700 bg-red-50/50 dark:bg-red-900/5 p-1">
                                                            <input type="text" className="w-full h-7 bg-white border rounded px-1 text-xs focus:ring-1 focus:ring-blue-500"
                                                                value={app.exam_password || ''} onChange={e => updateAppState(student.id, app.id, 'exam_password', e.target.value)} />
                                                        </td>

                                                        <td className="px-2 py-2 border border-gray-200 dark:border-gray-700 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 font-medium p-1">
                                                            <select
                                                                className="w-full bg-white dark:bg-gray-800 border rounded py-1 px-1 text-xs focus:ring-1 focus:ring-blue-500"
                                                                value={app.status || ''}
                                                                onChange={e => updateAppState(student.id, app.id, 'status', e.target.value)}
                                                            >
                                                                {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                                                                <option value="Diğer">Diğer</option>
                                                            </select>
                                                        </td>

                                                        <td className="px-2 py-2 border border-gray-200 dark:border-gray-700 p-1">
                                                            <input type="text" className="w-full h-7 bg-white border rounded px-1 text-xs focus:ring-1 focus:ring-blue-500"
                                                                value={app.payment || ''} onChange={e => updateAppState(student.id, app.id, 'payment', e.target.value)} />
                                                        </td>

                                                        <td className="px-2 py-2 border border-gray-200 dark:border-gray-700 bg-red-50/50 dark:bg-red-900/5 p-1">
                                                            <input type="text" className="w-full h-7 bg-white border rounded px-1 text-xs focus:ring-1 focus:ring-blue-500"
                                                                value={app.exam_date || ''} onChange={e => updateAppState(student.id, app.id, 'exam_date', e.target.value)} />
                                                        </td>

                                                        <td className="px-2 py-2 border border-gray-200 dark:border-gray-700 p-1">
                                                            <input type="text" className="w-full h-7 bg-white border rounded px-1 text-xs focus:ring-1 focus:ring-blue-500 font-bold text-blue-600"
                                                                value={app.result || ''} onChange={e => updateAppState(student.id, app.id, 'result', e.target.value)} />
                                                        </td>

                                                        <td className="px-2 py-2 border border-gray-200 dark:border-gray-700 bg-red-50/50 dark:bg-red-900/5 p-1">
                                                            <input type="text" className="w-full h-7 bg-white border rounded px-1 text-xs focus:ring-1 focus:ring-blue-500"
                                                                value={app.portal_username || ''} onChange={e => updateAppState(student.id, app.id, 'portal_username', e.target.value)} />
                                                        </td>

                                                        <td className="px-2 py-2 border border-gray-200 dark:border-gray-700 p-1">
                                                            <input type="text" className="w-full h-7 bg-white border rounded px-1 text-xs focus:ring-1 focus:ring-blue-500"
                                                                value={app.portal_password || ''} onChange={e => updateAppState(student.id, app.id, 'portal_password', e.target.value)} />
                                                        </td>

                                                        <td className="px-2 py-2 border border-gray-200 dark:border-gray-700 bg-red-50/50 dark:bg-red-900/5 p-1">
                                                            <input type="text" className="w-full h-7 bg-white border rounded px-1 text-xs focus:ring-1 focus:ring-blue-500"
                                                                value={app.registration_status || ''} onChange={e => updateAppState(student.id, app.id, 'registration_status', e.target.value)} />
                                                        </td>

                                                        <td className="px-2 py-2 border border-gray-200 dark:border-gray-700 text-center flex gap-1 justify-center items-center">
                                                            <button onClick={() => handleSave(student)} className="text-green-600 hover:bg-green-100 p-1 rounded transition-colors" title="Kaydet"><Save size={16} /></button>
                                                            <button onClick={() => handleDelete(student.id, app.id)} className="text-red-600 hover:bg-red-100 p-1 rounded transition-colors" title="Sil"><Trash2 size={16} /></button>
                                                            {index === apps.length - 1 && (
                                                                <button onClick={() => handleAddApplication(student.id)} className="text-blue-600 hover:bg-blue-100 p-1 rounded transition-colors" title="Yeni Ekle"><Plus size={16} /></button>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default MedicalStudentList;
